import * as Icons from '@ant-design/icons';
import useNotification from 'hooks/useNotification';
import {
  Button,
  Collapse,
  Drawer,
  Empty,
  Flex,
  Image,
  Skeleton,
  Tag,
  Typography
} from 'antd';
import { DocumentPanel } from './DocumentPanel';
import { FieldsetTitle } from 'components/professor_area/FieldsetTitle';
import { ICertificateEnrollment } from 'interfaces/course_certificate';
import { IDocument } from 'interfaces/documentation/document';
import { IDocumentTemplate } from 'interfaces/documentation/document-template';
import { STATUS_MAP } from 'constants/status-map';
import { download } from 'services/documents';
import { useCallback, useMemo, useState } from 'react';
import { useCertificateStore } from '../../../certificate.store';
import { useNavigation, useOne } from '@refinedev/core';

interface ISectionSentDocumentsProps {
  templates: IDocumentTemplate[];
}

export const SectionSentDocuments = ({
  templates
}: ISectionSentDocumentsProps) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [fileUrl, setFileUrl] = useState<string>('');
  const [openedFile, setOpenedFile] = useState<IDocument | null>(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [openPanels, setOpenPanels] = useState<string[]>([]);
  const { showError } = useNotification();
  const { showUrl } = useNavigation();

  const { enrollment, setEnrollment } = useCertificateStore();
  const {
    documentation,
    documentation_id,
    documentation_status = 'pending'
  } = enrollment!;

  const documents = useMemo(
    () => documentation?.documents ?? [],
    [documentation]
  );

  const mergedDocumentsWithTemplates = useMemo(() => {
    if (!documents && !templates) {
      return [];
    }

    return documents?.map((document: IDocument) => {
      const template = templates?.find(
        template => template.id === document.template_id
      );

      return {
        ...document,
        name: template?.name ?? 'Nenhum template vinculado'
      };
    });
  }, [documents, templates]);

  const sortedDocuments = useMemo(
    () =>
      mergedDocumentsWithTemplates
        ?.filter((document: IDocument) => document.status === 'approved')
        ?.sort((a: IDocument, b: IDocument) => {
          const aHasFields = a.attribute_fields.length > 0;
          const bHasFields = b.attribute_fields.length > 0;

          // Primeiro os que têm campos, depois os que não têm
          if (aHasFields && !bHasFields) {
            return -1;
          }

          if (!aHasFields && bHasFields) {
            return 1;
          }
          return 0;
        }),
    [mergedDocumentsWithTemplates]
  );

  // Função para controlar quais panels estão abertos
  const handlePanelChange = useCallback((keys: string | string[]) => {
    const keyArray = Array.isArray(keys) ? keys : [keys];
    setOpenPanels(keyArray.filter(Boolean));
  }, []);

  const getFile = async (documentId: string) => {
    try {
      setLoadingFile(true);
      const fileData = await download(documentId);
      setFileUrl(fileData.url);
    } catch {
      showError('Erro ao recuperar arquivo. Tente novamente.');
    } finally {
      setLoadingFile(false);
    }
  };

  const viewFile = (document: IDocument) => {
    setOpenDrawer(true);
    setOpenedFile(document);
    getFile(document.id);
  };

  // Hook para buscar dados atualizados do enrollment
  const { refetch: refetchEnrollment } = useOne({
    resource: 'graduate/courses/enrollments',
    id: enrollment?.id as string,
    queryOptions: { enabled: false }
  });

  // Callback para atualizar os dados após edição de documento
  const handleDocumentUpdated = useCallback(async () => {
    if (enrollment?.id) {
      const { data: updatedData } = await refetchEnrollment();

      if (updatedData?.data) {
        setEnrollment(updatedData.data as ICertificateEnrollment);
      }
    }
  }, [enrollment?.id, setEnrollment, refetchEnrollment]);

  return (
    <>
      <fieldset>
        <Flex justify="space-between" align="center" className="mb-6">
          <FieldsetTitle
            title="Documentações enviadas"
            icon={Icons.FileDoneOutlined}
            className="mb-0"
            hint={
              <Flex gap={8} align="center">
                Status:
                <Tag color={STATUS_MAP[documentation_status]?.color}>
                  {STATUS_MAP[documentation_status]?.text}
                </Tag>
              </Flex>
            }
          />

          {documentation_id && (
            <Button
              iconPosition="end"
              icon={<Icons.ExportOutlined />}
              onClick={() => {
                window.open(showUrl('documentations', documentation_id));
              }}
            >
              Ir para documentos
            </Button>
          )}
        </Flex>

        {sortedDocuments?.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Flex vertical gap={8} className="max-w-screen-md mx-auto">
                <Typography.Text strong type="secondary">
                  Nenhum documento enviado
                </Typography.Text>
                <Typography.Text type="secondary">
                  Para visualizar os documentos do aluno aqui, é necessário que
                  eles tenham sido enviados e aprovados.
                </Typography.Text>
              </Flex>
            }
          />
        )}

        <Collapse
          ghost
          bordered={false}
          activeKey={openPanels}
          onChange={handlePanelChange}
        >
          {sortedDocuments?.map((document: IDocument) => {
            const isPanelOpen = openPanels.includes(document.id);

            return (
              <DocumentPanel
                key={document.id}
                document={document}
                isPanelOpen={isPanelOpen}
                enrollmentCertificateStatus={enrollment?.certificate_status}
                documentationId={documentation_id}
                onViewFile={viewFile}
                onDocumentUpdated={handleDocumentUpdated}
              />
            );
          })}
        </Collapse>
      </fieldset>

      <Drawer
        className="!p-0"
        title={
          <Flex vertical>
            <Typography.Title level={4} className="!mb-0">
              Visualizar de documentos
            </Typography.Title>
            <Typography.Text type="secondary" className="font-normal">
              Acompanhe o histórico de todas as vias emitidas
            </Typography.Text>
          </Flex>
        }
        extra={
          <Button
            variant="text"
            color="default"
            icon={<Icons.CloseOutlined />}
            onClick={() => setOpenDrawer(false)}
          ></Button>
        }
        closable={false}
        closeIcon={false}
        onClose={() => setOpenDrawer(false)}
        open={openDrawer}
        width={'50%'}
      >
        <div className="relative flex items-center justify-center min-h-full">
          {loadingFile ? (
            <Skeleton.Image active className="!w-full h-full min-h-[500px]" />
          ) : (
            <>
              {openedFile?.file_name.includes('pdf') ? (
                <iframe
                  src={fileUrl}
                  className="absolute top-0 lef-0 overflow-y-auto overflow-x-hidden w-full h-full flex-1"
                />
              ) : (
                <Image src={fileUrl} />
              )}
            </>
          )}
        </div>
      </Drawer>
    </>
  );
};
